import React from 'react';
import { Link } from 'react-router-dom';
import Badge from '../ui/Badge';
import type { ReconciliationStrategyRow } from '../../types/reconciliation';

interface Props {
  strategies: ReconciliationStrategyRow[];
}

const sideVariant = (side: string): 'success' | 'danger' | 'warning' | 'default' => {
  if (side === 'BUY') return 'success';
  if (side === 'SELL') return 'danger';
  if (side === 'MIXED') return 'warning';
  return 'default';
};

const formatSignedQty = (q: number): string => {
  if (q > 0) return `+${q}`;
  if (q < 0) return `${q}`;
  return '0';
};

const qtyColor = (q: number): string => {
  if (q > 0) return 'text-green-600 dark:text-green-400';
  if (q < 0) return 'text-red-500 dark:text-red-400';
  return 'text-gray-700 dark:text-gray-300';
};

const ReconciliationStrategyBreakdown: React.FC<Props> = ({ strategies }) => {
  if (!strategies.length) {
    return (
      <div className="px-4 sm:px-6 py-4 text-center text-xs text-gray-500 dark:text-gray-400">
        No platform positions for this instrument — broker has it but the platform does not.
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 bg-gray-50 dark:bg-gray-900/40">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
        Platform breakdown ({strategies.length} {strategies.length === 1 ? 'strategy' : 'strategies'})
      </div>

      {/* Mobile — stacked cards (4-column table is too wide inside the
          already-narrow expanded card) */}
      <div className="block sm:hidden divide-y divide-gray-200 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        {strategies.map(s => (
          <div key={s.strategyId} className="px-3 py-2.5">
            {/* Top: strategy name + side + signed qty */}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-xs text-gray-900 dark:text-white truncate">
                  {s.strategyName || <span className="text-gray-400 italic">Unnamed strategy</span>}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {s.basketName || <span className="italic">Unnamed basket</span>}
                </div>
              </div>
              {s.side ? (
                <Badge variant={sideVariant(s.side)} size="sm">{s.side}</Badge>
              ) : (
                <span className="text-gray-400 text-xs">—</span>
              )}
              <span className={`tabular-nums font-semibold text-xs ${qtyColor(s.signedQty)}`}>
                {formatSignedQty(s.signedQty)}
              </span>
            </div>
            {/* Position chips */}
            {s.positionIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {s.positionIds.slice(0, 3).map(pid => (
                  <Link
                    key={pid}
                    to="/schedule?view=positions"
                    title={pid}
                    className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 font-mono text-[10px]"
                  >
                    {pid.slice(-8)}
                  </Link>
                ))}
                {s.positionIds.length > 3 && (
                  <span className="text-gray-500 dark:text-gray-400 text-[10px] self-center">
                    +{s.positionIds.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop — 4-column table */}
      <div className="hidden sm:block overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Basket / Strategy
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Side
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Signed Qty
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Positions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {strategies.map(s => (
              <tr key={s.strategyId} className="bg-white dark:bg-gray-800">
                <td className="px-4 py-3 text-xs">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {s.strategyName || <span className="text-gray-400 italic">Unnamed strategy</span>}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                    {s.basketName || <span className="italic">Unnamed basket</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">
                  {s.side ? (
                    <Badge variant={sideVariant(s.side)} size="sm">{s.side}</Badge>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className={`px-4 py-3 text-xs text-right tabular-nums font-semibold ${qtyColor(s.signedQty)}`}>
                  {formatSignedQty(s.signedQty)}
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="flex flex-wrap gap-1">
                    {s.positionIds.slice(0, 3).map(pid => (
                      <Link
                        key={pid}
                        to="/schedule?view=positions"
                        title={pid}
                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 font-mono text-[10px] truncate max-w-[8rem]"
                      >
                        {pid.slice(-8)}
                      </Link>
                    ))}
                    {s.positionIds.length > 3 && (
                      <span className="text-gray-500 dark:text-gray-400 text-[10px] self-center">
                        +{s.positionIds.length - 3}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReconciliationStrategyBreakdown;
